/**
 * Adjustment (adjust_lot) & Bank cost change — Virtuoso + Search, template saleContract.
 * SQL: `query_FuturesQryAdjustLot.txt`, `query_FuturesQryBankCostChange.txt`.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useCallback, useMemo, useState, type CSSProperties } from 'react'
import dayjs from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useCreateAdjustLotMutation,
  useCreateBankCostChangeMutation,
  useDeleteAdjustLotMutation,
  useDeleteBankCostChangeMutation,
  useGetAdjustLotRowsQuery,
  useGetBankCostChangeRowsQuery,
  useGetAllBanksQuery,
  useGetLocalPAdjustLotOptionsQuery,
  useGetLocalSAdjustLotOptionsQuery,
  useUpdateAdjustLotMutation,
  useUpdateBankCostChangeMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import { useFuturesColumns } from './useFuturesColumns'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresFormDialogTitleTextSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
} from './futuresAccessUi'

/* filterBarBtnSx imported từ template */

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** Cột freeze khi scroll ngang — Adjustment: contractNo (1 col). */
const FROZEN_KEYS_ADJUST: ReadonlySet<string> = new Set(['contractNo'])
const FROZEN_FALLBACK_WIDTH_ADJUST: Record<string, number> = {
  contractNo: 140,
}
/** Bank Cost Change: freeze Date + Bank (2 col). Date là col 1 (left:0),
 *  Bank là col 2 (offset = width(date)). */
const FROZEN_KEYS_BCC_FIRST = 'date'
const FROZEN_KEYS_BCC_SECOND = 'bank'
const FROZEN_FALLBACK_WIDTH_BCC: Record<string, number> = {
  date: 110,
  bank: 90,
}

const COLUMNS_ADJUST: ColumnDef[] = [
  { key: 'contractNo', label: 'Contract No' },
  { key: 'adjustedLot', label: 'Adjusted Lot', kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_ADJUST = new Set<string>()

// Thứ tự bám datasheet Access `frmFuturesQryBankCostChange` (TabIndex):
// date · bank · ex · cost · date_updated · person_updated · bank_cost_change_id.
// `bankCostChangeId` là cột CUỐI ở Access (trước đây web để đầu bảng).
// 5 cột audit thô (createdDate/updatedDate/createdBy/updatedBy/isStatus) là
// web-only, không có trong Access → dồn xuống cuối.
const COLUMNS_BANK_COST: ColumnDef[] = [
  { key: 'date', label: 'Date', kind: 'date' },
  { key: 'bank', label: 'Bank' },
  { key: 'ex', label: 'Ex' },
  { key: 'cost', label: 'Cost', kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated' },
  { key: 'bankCostChangeId', label: 'ID', kind: 'number', align: 'right' },
  { key: 'createdDate', label: 'Created', kind: 'datetime' },
  { key: 'updatedDate', label: 'Updated', kind: 'datetime' },
  { key: 'createdBy', label: 'Created by' },
  { key: 'updatedBy', label: 'Updated by' },
  { key: 'isStatus', label: 'Status', kind: 'number', align: 'right' },
]

const NON_SUM_BANK_COST = new Set<string>(['bankCostChangeId', 'isStatus'])

const STORAGE_COLS_ADJUST = 'futures-cols-adjust-lot-v1'
const STORAGE_COLS_BANK_COST_GRID = 'futures-cols-bank-cost-change-v1'

// Access `frmFuturesQryBankCostChangeAdd.ex` = Value List 2 cột, BoundColumn=1:
// 2 dòng chọn — LR (hiện "LR — Robusta"), KC (hiện "KC — Arabica"); lưu cột 1.
const BANK_COST_EX_OPTIONS: { value: string; label: string }[] = [
  { value: 'LR', label: 'Robusta' },
  { value: 'KC', label: 'Arabica' },
]

type ContractPickKind = 'P' | 'S'
type ContractPickOption = {
  kind: ContractPickKind
  contractNo: string
  contractDate: string
  party: string
  quality: string
  totalLot: number | null
  label: string
}

type FilterKind = 'search'
type GridRow = Record<string, unknown> & { id: string }

function str(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

function cellValue(row: Record<string, unknown>, col: ColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  return String(v)
}

function sumNumericOnPage(pageRows: GridRow[], col: ColumnDef, nonSum: Set<string>): number | null {
  if (col.kind !== 'number' || nonSum.has(col.key)) return null
  let total = 0
  let count = 0
  for (const row of pageRows) {
    const v = row[col.key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      count += 1
    }
  }
  return count > 0 ? total : null
}

/* ─── Adjust lot ─── */

export function FuturesAdjustLotListView({ embedded: _embedded }: { embedded?: boolean }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()

  const queryArgs = {
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  }
  const { data, isLoading, isFetching, error, refetch } = useGetAdjustLotRowsQuery(queryArgs)
  const [createRow, c1] = useCreateAdjustLotMutation()
  const [updateRow, c2] = useUpdateAdjustLotMutation()
  const [deleteRow, c3] = useDeleteAdjustLotMutation()

  const columns = COLUMNS_ADJUST
  const nonSum = NON_SUM_ADJUST

  const columnsCtrl = useFuturesColumns({
    mode: 'adjust-lot',
    allColumns: columns,
    storageKey: STORAGE_COLS_ADJUST,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.adjust-bank-cost.adjust-lot')

  const baseRows = useMemo((): GridRow[] => {
    const items = data?.items ?? []
    return items.map((r, idx) => {
      const rec = r as Record<string, unknown>
      const pk = str(rec.contractNo ?? '')
      return { ...rec, id: `${idx}-${pk}` }
    })
  }, [data?.items])

  /* ── Filter pipeline ────────────────────────────────────────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters, columns],
  )

  const rows = useMemo((): GridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  const firstRow = useMemo((): GridRow | null => {
    if (!selectedRowId) return null
    return baseRows.find((r) => r.id === selectedRowId) ?? null
  }, [baseRows, selectedRowId])

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  const totalRowValues = useMemo(
    () => new Map(visibleColumns.map((col) => [col.key, sumNumericOnPage(rows, col, nonSum)] as const)),
    [rows, visibleColumns, nonSum],
  )

  const gridKey = `adj:${pagination.page}:${pagination.pageSize}:${rows.length}`
  const minTableWidth = visibleColumns.length * 96


  const [dialogOpen, setDialogOpen] = useState(false)
  const [creating, setCreating] = useState(true)
  const [contractNo, setContractNo] = useState('')
  const [contractKind, setContractKind] = useState<ContractPickKind | null>(null)
  const [adjustedLot, setAdjustedLot] = useState('')
  const [deletePk, setDeletePk] = useState<string | null>(null)
  const [adjTouched, setAdjTouched] = useState(false)

  const localPContractsQ = useGetLocalPAdjustLotOptionsQuery(undefined, {
    skip: !dialogOpen || !creating,
  })
  const localSContractsQ = useGetLocalSAdjustLotOptionsQuery(undefined, {
    skip: !dialogOpen || !creating,
  })

  const localPOptions = useMemo((): ContractPickOption[] => {
    const out: ContractPickOption[] = []
    const seen = new Set<string>()
    for (const it of localPContractsQ.data ?? []) {
      const cn = str(it.contractNo)
      if (!cn || seen.has(cn)) continue
      seen.add(cn)
      const cdRaw = str(it.contractDate)
      const party = str(it.objectName)
      const cdLabel = formatAccessDate(cdRaw)
      const parts = [cdLabel, party].filter(Boolean)
      out.push({
        kind: 'P',
        contractNo: cn,
        contractDate: cdRaw,
        party,
        quality: '',
        totalLot: null,
        label: parts.length ? `${cn} — ${parts.join(' • ')}` : cn,
      })
    }
    return out
  }, [localPContractsQ.data])

  const localSOptions = useMemo((): ContractPickOption[] => {
    const out: ContractPickOption[] = []
    const seen = new Set<string>()
    for (const it of localSContractsQ.data ?? []) {
      const cn = str(it.contractNo)
      if (!cn || seen.has(cn)) continue
      seen.add(cn)
      const cdRaw = str(it.contractDate)
      const party = str(it.objectName)
      const cdLabel = formatAccessDate(cdRaw)
      const parts = [cdLabel, party].filter(Boolean)
      out.push({
        kind: 'S',
        contractNo: cn,
        contractDate: cdRaw,
        party,
        quality: '',
        totalLot: null,
        label: parts.length ? `${cn} — ${parts.join(' • ')}` : cn,
      })
    }
    return out
  }, [localSContractsQ.data])

  const existingAdjustSet = useMemo(() => {
    const s = new Set<string>()
    for (const r of rows) {
      const cn = str(r['contractNo'])
      if (cn) s.add(cn)
    }
    return s
  }, [rows])

  const openNew = () => {
    setCreating(true)
    setContractNo('')
    setContractKind(null)
    setAdjustedLot('')
    setAdjTouched(false)
    setDialogOpen(true)
  }

  const openEdit = () => {
    const r = firstRow
    if (!r) {
      snack.warning('Please choose a record!')
      return
    }
    setCreating(false)
    setContractNo(str(r['contractNo']))
    setContractKind(null)
    setAdjustedLot(str(r['adjustedLot']))
    setAdjTouched(false)
    setDialogOpen(true)
  }

  // Access `frmFuturesQryAdjustLotAdd.adjusted_lot` Format="0" — cột DB là INTEGER,
  // số lẻ sẽ bị Postgres round âm thầm nên chặn từ FE.
  const adjFieldErrors = useMemo(() => ({
    contractNo: !contractNo.trim(),
    adjustedLot: !adjustedLot.trim() || !Number.isInteger(Number(adjustedLot)),
  }), [contractNo, adjustedLot])

  const save = async () => {
    setAdjTouched(true)
    if (adjFieldErrors.contractNo) {
      snack.error('Please choose Contract No!')
      return
    }
    if (adjFieldErrors.adjustedLot) {
      snack.error(adjustedLot.trim() ? 'Adjusted lot must be a whole number!' : 'Please type Adjusted lot!')
      return
    }
    try {
      if (creating) {
        await createRow({ contractNo: contractNo.trim(), adjustedLot: adjustedLot ? Number(adjustedLot) : undefined }).unwrap()
      } else {
        await updateRow({
          contractNo: contractNo.trim(),
          body: { adjustedLot: adjustedLot ? Number(adjustedLot) : undefined },
        }).unwrap()
      }
      snack.success('Saved.')
      setDialogOpen(false)
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const confirmDelete = async () => {
    if (!deletePk) return
    try {
      await deleteRow(deletePk).unwrap()
      snack.success('Row soft-deleted.')
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const busy = c1.isLoading || c2.isLoading || c3.isLoading
  const deleteLoading = c3.isLoading

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
          '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
        }}
      >
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
              <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
              <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!canEdit || !firstRow} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !firstRow?.['contractNo']}
                onClick={() => {
                  const cn = str(firstRow?.['contractNo'])
                  if (!cn) {
                    snack.warning('Please choose a record!')
                    return
                  }
                  setDeletePk(cn)
                }}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>
        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="Contract no, lot, user…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>
          <DataframeBody loading={isLoading} fetching={isFetching}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load adjustment"
                description="Try again or contact support if the error persists."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && rows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : rows.length === 0 ? (
              <BrandState title="No data" description="Adjust search or add a contract row." />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={rows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {visibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS_ADJUST.has(col.key)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_ADJUST[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            // Cho phép bôi đen + copy tên cột (header).
                            userSelect: 'text',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {visibleColumns.map((col, index) => {
                      const isFrozen = FROZEN_KEYS_ADJUST.has(col.key)
                      const total = totalRowValues.get(col.key)
                      const val = total == null ? '' : formatAccessNumber(total)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_ADJUST[col.key] : undefined)
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {index === 0 ? 'Total' : val}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: GridRow) =>
                  visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS_ADJUST.has(col.key)
                    const val = cellValue(row, col)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_ADJUST[col.key] : undefined)
                    const wrap = getWidth(col.key) != null
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }
                components={{
                  Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                  TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                  TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                  TableRow: (props: any) => {
                    const idx = Number((props as Record<string, unknown>)['data-item-index'])
                    const r = Number.isFinite(idx) ? rows[idx] : null
                    const rid = r?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rid != null && rid === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => rid && toggleSelectRow(rid)}
                      />
                    )
                  },
                }}
              />
            )}
          </DataframeBody>
        </Box>
      </Box>

      <ConfirmDialog
        open={deletePk != null}
        title="Delete adjustment"
        message={deletePk ? `Soft-delete adjust lot for ${deletePk}?` : ''}
        danger
        loading={deleteLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onCancel={() => setDeletePk(null)}
        onClose={() => setDeletePk(null)}
        onConfirm={() => void confirmDelete()}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm" scroll="paper" PaperProps={futuresSaleContractDialogPaperProps}>
        <DialogTitle sx={futuresFormDialogTitleSx}>
          <Typography
            component="span"
            noWrap
            title={creating ? 'Add adjustment' : 'Edit adjustment'}
            sx={futuresFormDialogTitleTextSx}
          >
            {creating ? 'Add adjustment' : 'Edit adjustment'}
          </Typography>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              size="small"
              color="success"
              disabled={busy || !contractNo.trim() || (creating && existingAdjustSet.has(contractNo.trim()))}
              onClick={() => void save()}
              sx={futuresFormDialogActionBtnSx}
            >
              {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setDialogOpen(false)} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {creating ? (
              <>
                <Autocomplete
                  autoHighlight
                  options={localPOptions}
                  loading={localPContractsQ.isFetching}
                  value={contractKind === 'P' ? (localPOptions.find((o) => o.contractNo === contractNo) ?? null) : null}
                  onChange={(_, v) => {
                    if (v) {
                      setContractNo(v.contractNo)
                      setContractKind('P')
                    } else if (contractKind === 'P') {
                      setContractNo('')
                      setContractKind(null)
                    }
                  }}
                  getOptionLabel={(o) => o.contractNo}
                  isOptionEqualToValue={(opt, val) => opt.contractNo === val.contractNo}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase()
                    if (!q) return opts
                    return opts.filter(
                      (o) =>
                        o.contractNo.toLowerCase().includes(q) ||
                        o.party.toLowerCase().includes(q) ||
                        o.quality.toLowerCase().includes(q),
                    )
                  }}
                  renderOption={(props, option) => {
                    const cdLabel = formatAccessDate(option.contractDate)
                    const tlLabel = option.totalLot != null ? `${formatAccessNumber(option.totalLot)} lot` : ''
                    const meta = [cdLabel, option.party, option.quality, tlLabel].filter(Boolean).join(' • ')
                    return (
                      <li {...props} key={`P:${option.contractNo}`}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, width: '100%' }}>
                          <Box component="span" sx={{ fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>
                            {option.contractNo}
                          </Box>
                          {meta ? (
                            <Box
                              component="span"
                              sx={{
                                ml: 'auto',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                            >
                              {meta}
                            </Box>
                          ) : null}
                        </Box>
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contract P *"
                      size="small"
                      disabled={contractKind === 'S'}
                      placeholder={contractKind === 'S' ? 'Contract S selected — clear to switch to P' : 'Find Local P contract…'}
                      error={adjTouched && adjFieldErrors.contractNo && contractKind !== 'S'}
                      helperText={
                        adjTouched && adjFieldErrors.contractNo && contractKind !== 'S'
                          ? 'Please choose Contract P or Contract S from list'
                          : undefined
                      }
                    />
                  )}
                />
                <Autocomplete
                  autoHighlight
                  options={localSOptions}
                  loading={localSContractsQ.isFetching}
                  value={contractKind === 'S' ? (localSOptions.find((o) => o.contractNo === contractNo) ?? null) : null}
                  onChange={(_, v) => {
                    if (v) {
                      setContractNo(v.contractNo)
                      setContractKind('S')
                    } else if (contractKind === 'S') {
                      setContractNo('')
                      setContractKind(null)
                    }
                  }}
                  getOptionLabel={(o) => o.contractNo}
                  isOptionEqualToValue={(opt, val) => opt.contractNo === val.contractNo}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase()
                    if (!q) return opts
                    return opts.filter(
                      (o) =>
                        o.contractNo.toLowerCase().includes(q) ||
                        o.party.toLowerCase().includes(q) ||
                        o.quality.toLowerCase().includes(q),
                    )
                  }}
                  renderOption={(props, option) => {
                    const cdLabel = formatAccessDate(option.contractDate)
                    const tlLabel = option.totalLot != null ? `${formatAccessNumber(option.totalLot)} lot` : ''
                    const meta = [cdLabel, option.party, option.quality, tlLabel].filter(Boolean).join(' • ')
                    return (
                      <li {...props} key={`S:${option.contractNo}`}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, width: '100%' }}>
                          <Box component="span" sx={{ fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>
                            {option.contractNo}
                          </Box>
                          {meta ? (
                            <Box
                              component="span"
                              sx={{
                                ml: 'auto',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                            >
                              {meta}
                            </Box>
                          ) : null}
                        </Box>
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contract S *"
                      size="small"
                      disabled={contractKind === 'P'}
                      placeholder={contractKind === 'P' ? 'Contract P selected — clear to switch to S' : 'Find Local S contract…'}
                      error={adjTouched && adjFieldErrors.contractNo && contractKind !== 'P'}
                      helperText={
                        adjTouched && adjFieldErrors.contractNo && contractKind !== 'P'
                          ? 'Please choose Contract P or Contract S from list'
                          : undefined
                      }
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  * Choose: Contract P or Contract S. 
                  {contractNo.trim() && existingAdjustSet.has(contractNo.trim()) ? (
                    <Box component="span" sx={{ color: 'error.main', display: 'block', mt: 0.25 }}>
                      Adjustment already exists for this contract — Save will fail with PK conflict.
                    </Box>
                  ) : null}
                </Typography>
              </>
            ) : (
              <TextField label="Contract No *" value={contractNo} size="small" fullWidth disabled />
            )}
            <TextField
              label="Adjusted lot *"
              value={adjustedLot}
              onChange={(e) => setAdjustedLot(e.target.value)}
              size="small"
              fullWidth
              type="number"
              inputProps={{ step: 1, inputMode: 'numeric' }}
              error={adjTouched && adjFieldErrors.adjustedLot}
              helperText={
                adjTouched && adjFieldErrors.adjustedLot
                  ? adjustedLot.trim()
                    ? 'Adjusted lot must be a whole number'
                    : 'Please type Adjusted lot'
                  : undefined
              }
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ─── Bank cost change ─── */

export function FuturesBankCostChangeListView({ embedded: _embedded }: { embedded?: boolean }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()

  const queryArgs = {
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  }
  const { data, isLoading, isFetching, error, refetch } = useGetBankCostChangeRowsQuery(queryArgs)
  const [createRow, c1] = useCreateBankCostChangeMutation()
  const [updateRow, c2] = useUpdateBankCostChangeMutation()
  const [deleteRow, c3] = useDeleteBankCostChangeMutation()

  const columns = COLUMNS_BANK_COST
  const nonSum = NON_SUM_BANK_COST

  const bccColumnsCtrl = useFuturesColumns({
    mode: 'bank-cost-change',
    allColumns: columns,
    storageKey: STORAGE_COLS_BANK_COST_GRID,
  })
  const { visibleColumns: bccVisibleColumns, openMenu: openBccColMenu } = bccColumnsCtrl
  const { getWidth: getBccWidth, startResize: startBccResize } = useColumnWidths('futures.adjust-bank-cost.bank-cost-change')

  const baseRows = useMemo((): GridRow[] => {
    const items = data?.items ?? []
    return items.map((r, idx) => {
      const rec = r as Record<string, unknown>
      const pk = str(rec.bankCostChangeId ?? '')
      return { ...rec, id: `${idx}-${pk}` }
    })
  }, [data?.items])

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters, columns],
  )

  const rows = useMemo((): GridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  const firstRow = useMemo((): GridRow | null => {
    if (!selectedRowId) return null
    return baseRows.find((r) => r.id === selectedRowId) ?? null
  }, [baseRows, selectedRowId])

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  const totalRowValues = useMemo(
    () => new Map(bccVisibleColumns.map((col) => [col.key, sumNumericOnPage(rows, col, nonSum)] as const)),
    [rows, bccVisibleColumns, nonSum],
  )

  const gridKey = `bcc:${pagination.page}:${pagination.pageSize}:${rows.length}`
  const minTableWidth = bccVisibleColumns.length * 88

  /** Offset trái cho cột freeze thứ 2 (`bank`) = width hiện tại của cột `date`.
   *  Nếu user ẩn cột `date`, fallback về 0 (cột `bank` thành col đầu sticky). */
  const bccSecondFrozenLeft = useMemo(() => {
    const dateVisible = bccVisibleColumns.some((c) => String(c.key) === FROZEN_KEYS_BCC_FIRST)
    if (!dateVisible) return 0
    return getBccWidth(FROZEN_KEYS_BCC_FIRST) ?? FROZEN_FALLBACK_WIDTH_BCC[FROZEN_KEYS_BCC_FIRST] ?? 120
  }, [bccVisibleColumns, getBccWidth])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [creating, setCreating] = useState(true)
  const [rowId, setRowId] = useState<number | null>(null)
  const [date, setDate] = useState('')
  const [bank, setBank] = useState('')
  const [ex, setEx] = useState('')
  const [cost, setCost] = useState('')
  const [deletePk, setDeletePk] = useState<number | null>(null)
  const [bccTouched, setBccTouched] = useState(false)

  // Bank options từ DB — Access `frmFuturesQryBankCostChangeAdd.bank` RowSource
  // `SELECT bank FROM public_bank WHERE is_status=0 ORDER BY bank` (MỌI bank
  // active, KHÔNG lọc IN như partners).
  const bankOptionsQ = useGetAllBanksQuery(undefined, { skip: !dialogOpen })
  const bankSelectChoices = useMemo(() => {
    const fromDb = (bankOptionsQ.data ?? []).map((b) => String(b)).filter(Boolean)
    // Giữ giá trị legacy của row đang edit nếu DB không trả về (đề phòng dữ liệu cũ).
    if (bank && !fromDb.includes(bank)) return [bank, ...fromDb]
    return fromDb
  }, [bankOptionsQ.data, bank])

  const exSelectChoices = useMemo(() => {
    const base = [...BANK_COST_EX_OPTIONS]
    // Giữ giá trị legacy của row đang edit nếu không nằm trong 2 option chuẩn.
    if (ex && !base.some((o) => o.value === ex)) {
      return [{ value: ex, label: '' }, ...base]
    }
    return base
  }, [ex])

  const openNew = () => {
    setCreating(true)
    setRowId(null)
    // Auto-load Date = Now (today) khi New — user chỉ cần đổi nếu khác.
    setDate(dayjs().format('YYYY-MM-DD'))
    setBank('')
    setEx('')
    setCost('')
    setBccTouched(false)
    setDialogOpen(true)
  }

  const openEdit = () => {
    const r = firstRow
    if (!r) {
      snack.warning('Please choose a record!')
      return
    }
    setCreating(false)
    setRowId(Number(r['bankCostChangeId']))
    setDate(str(r['date']).slice(0, 10))
    setBank(str(r['bank']))
    setEx(str(r['ex']))
    setCost(str(r['cost']))
    setBccTouched(false)
    setDialogOpen(true)
  }

  const bccFieldErrors = useMemo(() => ({
    date: !date,
    bank: !bank.trim() || !bankSelectChoices.includes(bank.trim()),
  }), [date, bank, bankSelectChoices])

  const save = async () => {
    setBccTouched(true)
    if (bccFieldErrors.date) {
      snack.error('Please choose Date!')
      return
    }
    if (bccFieldErrors.bank) {
      snack.error('Please choose Bank from list!')
      return
    }
    try {
      if (creating) {
        await createRow({
          date,
          bank: bank.trim(),
          ex: ex || undefined,
          cost: cost ? Number(cost) : undefined,
        }).unwrap()
      } else if (rowId != null) {
        await updateRow({
          id: rowId,
          body: {
            date,
            bank: bank.trim(),
            ex: ex || undefined,
            cost: cost ? Number(cost) : undefined,
          },
        }).unwrap()
      }
      snack.success('Saved.')
      setDialogOpen(false)
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const confirmDelete = async () => {
    if (deletePk == null) return
    try {
      await deleteRow(deletePk).unwrap()
      snack.success('Row soft-deleted.')
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const busy = c1.isLoading || c2.isLoading || c3.isLoading
  const deleteLoading = c3.isLoading

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
          '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
        }}
      >
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
              <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
              <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!canEdit || !firstRow} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !firstRow?.['bankCostChangeId']}
                onClick={() => {
                  const id = Number(firstRow?.['bankCostChangeId'])
                  if (!id) {
                    snack.warning('Please choose a record!')
                    return
                  }
                  setDeletePk(id)
                }}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openBccColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>
        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="ID, date, bank, ex, cost…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={bccColumnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>
          <DataframeBody loading={isLoading} fetching={isFetching}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load bank cost change"
                description="Try again or contact support if the error persists."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && rows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : rows.length === 0 ? (
              <BrandState title="No data" description="Adjust search or create a row." />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={rows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {bccVisibleColumns.map((col) => {
                      const isFirst = col.key === FROZEN_KEYS_BCC_FIRST
                      const isSecond = col.key === FROZEN_KEYS_BCC_SECOND
                      const isFrozen = isFirst || isSecond
                      const w = getBccWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_BCC[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = isSecond ? bccSecondFrozenLeft : 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            // Cho phép bôi đen + copy tên cột (header).
                            userSelect: 'text',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startBccResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {bccVisibleColumns.map((col, index) => {
                      const isFirst = col.key === FROZEN_KEYS_BCC_FIRST
                      const isSecond = col.key === FROZEN_KEYS_BCC_SECOND
                      const isFrozen = isFirst || isSecond
                      const total = totalRowValues.get(col.key)
                      const val = total == null ? '' : formatAccessNumber(total)
                      const w = getBccWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_BCC[col.key] : undefined)
                      const cellStyle: CSSProperties = {}
                      if (w) { cellStyle.width = w; cellStyle.minWidth = w; cellStyle.maxWidth = w }
                      if (isSecond) cellStyle.left = bccSecondFrozenLeft
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFirst ? 'app-sticky-col' : isSecond ? 'app-sticky-col-2' : undefined}
                          style={Object.keys(cellStyle).length ? cellStyle : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {index === 0 ? 'Total' : val}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: GridRow) =>
                  bccVisibleColumns.map((col) => {
                    const isFirst = col.key === FROZEN_KEYS_BCC_FIRST
                    const isSecond = col.key === FROZEN_KEYS_BCC_SECOND
                    const isFrozen = isFirst || isSecond
                    const val = cellValue(row, col)
                    const w = getBccWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH_BCC[col.key] : undefined)
                    const wrap = getBccWidth(col.key) != null
                    const cellStyle: CSSProperties = {}
                    if (w) { cellStyle.width = w; cellStyle.minWidth = w; cellStyle.maxWidth = w }
                    if (isSecond) cellStyle.left = bccSecondFrozenLeft
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFirst ? 'app-sticky-col' : isSecond ? 'app-sticky-col-2' : undefined}
                          style={Object.keys(cellStyle).length ? cellStyle : undefined}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFirst ? 'app-sticky-col' : isSecond ? 'app-sticky-col-2' : undefined}
                        style={Object.keys(cellStyle).length ? cellStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }
                components={{
                  Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                  TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                  TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                  TableRow: (props: any) => {
                    const idx = Number((props as Record<string, unknown>)['data-item-index'])
                    const r = Number.isFinite(idx) ? rows[idx] : null
                    const rid = r?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rid != null && rid === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => rid && toggleSelectRow(rid)}
                      />
                    )
                  },
                }}
              />
            )}
          </DataframeBody>
        </Box>
      </Box>

      <ConfirmDialog
        open={deletePk != null}
        title="Delete bank cost change"
        message="Soft-delete this row?"
        danger
        loading={deleteLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onCancel={() => setDeletePk(null)}
        onClose={() => setDeletePk(null)}
        onConfirm={() => void confirmDelete()}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs" scroll="paper" PaperProps={futuresSaleContractDialogPaperProps}>
        <DialogTitle sx={futuresFormDialogTitleSx}>
          <Typography
            component="span"
            noWrap
            title={creating ? 'Add bank cost change' : 'Edit bank cost change'}
            sx={futuresFormDialogTitleTextSx}
          >
            {creating ? 'Add bank cost change' : 'Edit bank cost change'}
          </Typography>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              size="small"
              color="success"
              disabled={busy || !date || !bank.trim() || (!creating && rowId == null)}
              onClick={() => void save()}
              sx={futuresFormDialogActionBtnSx}
            >
              {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setDialogOpen(false)} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <DatePicker
              label="Date *"
              format="DD-MMM-YYYY"
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              disabled={!creating}
              slotProps={{ textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                error: bccTouched && bccFieldErrors.date,
                helperText: bccTouched && bccFieldErrors.date ? 'Please choose Date' : undefined,
              } }}
            />
            <Autocomplete
              size="small"
              autoHighlight
              options={bankSelectChoices}
              value={bank || null}
              onChange={(_, v) => setBank(v ?? '')}
              isOptionEqualToValue={(opt, val) => opt === val}
              disabled={!creating || bankOptionsQ.isFetching}
              loading={bankOptionsQ.isFetching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bank *"
                  size="small"
                  fullWidth
                  error={bccTouched && bccFieldErrors.bank}
                  helperText={
                    bccTouched && bccFieldErrors.bank
                      ? 'Please choose Bank from list'
                      : bankOptionsQ.isError
                        ? 'Failed to load bank list — please retry.'
                        : bankOptionsQ.isFetching
                          ? 'Loading…'
                          : ' '
                  }
                />
              )}
            />
            <Autocomplete
              size="small"
              autoHighlight
              options={exSelectChoices}
              value={exSelectChoices.find((o) => o.value === ex) ?? null}
              onChange={(_, v) => setEx(v?.value ?? '')}
              getOptionLabel={(o) => o.value}
              renderOption={(liProps, o) => (
                <li {...liProps} key={o.value}>{o.label ? `${o.value} — ${o.label}` : o.value}</li>
              )}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              // Search theo cả label (Robusta/Arabica) — getOptionLabel chỉ trả code LR/KC.
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase()
                if (!q) return opts
                return opts.filter((o) => `${o.value} ${o.label ?? ''}`.toLowerCase().includes(q))
              }}
              renderInput={(params) => (
                <TextField {...params} label="Ex" size="small" fullWidth />
              )}
            />
            <TextField label="Cost" value={cost} onChange={(e) => setCost(e.target.value)} size="small" fullWidth />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
